// Launch context handed over by the device that opened the caller.
//
// A kiosk embeds the caller in its own call window and opens the scan link with
// `?lang=<code>&source=kiosk`; the backend re-attaches both to the caller route it
// redirects to (`#/user-scan-qrcode/<token>/<details>?lang=en&source=kiosk`). Read
// ONCE, at module load, before any navigation drops the query from the hash.
//
// The context is also kept in sessionStorage for this window only, so a reload from
// a screen whose route no longer carries the query (the error screen's retry) does
// not fall back to the stored language or show the brand inside the kiosk. A fresh
// launch with its own query always wins over what was kept.
//
// The virtual assistant sends a guest who asks for a person back to the same scan route
// with `assistant=reception` in the query; kept the same way, so the assistant is not
// offered again in this window (see src/assistant.ts).

export type LaunchContext = {
  /** Language requested by the launching device (primary subtag, lower case). */
  lang: string | null
  /** The caller runs inside a kiosk's call window. */
  kiosk: boolean
  /** The guest came back from the virtual assistant asking for a person. */
  reception: boolean
}

const STORAGE_KEY = 'qr_launch_context'
const LANGUAGE = /^[a-z]{2}$/

const fromHash = (hash: string): LaunchContext | null => {
  const start = hash.indexOf('?')
  if (start < 0) return null
  const params = new URLSearchParams(hash.slice(start + 1))
  const lang = (params.get('lang') || '').trim().toLowerCase().slice(0, 2)
  const kiosk = params.get('source') === 'kiosk'
  const reception = params.get('assistant') === 'reception'
  if (!LANGUAGE.test(lang) && !kiosk && !reception) return null
  return { lang: LANGUAGE.test(lang) ? lang : null, kiosk, reception }
}

const readKept = (): LaunchContext | null => {
  try {
    const kept = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null')
    if (!kept || typeof kept !== 'object') return null
    return { lang: typeof kept.lang === 'string' && LANGUAGE.test(kept.lang) ? kept.lang : null, kiosk: kept.kiosk === true, reception: kept.reception === true }
  } catch {
    return null // storage unavailable or unreadable: the launch query is all there is
  }
}

const keep = (context: LaunchContext) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(context))
  } catch {
    // nowhere to keep it: this load still uses it
  }
}

const resolve = (): LaunchContext => {
  const launched = typeof window !== 'undefined' ? fromHash(window.location.hash) : null
  if (launched) {
    keep(launched)
    return launched
  }
  return readKept() || { lang: null, kiosk: false, reception: false }
}

export const launchContext: LaunchContext = resolve()

/** The caller runs inside a kiosk: no brand logo, and the kiosk decides the language. */
export const embeddedInKiosk: boolean = launchContext.kiosk

/** The guest came back from the virtual assistant asking for a person: do not offer it again. */
export const returnedFromAssistant = (): boolean => launchContext.reception
